// Single source of truth for the machine-translation engine catalogue used by
// every engine picker (NewProject, ProjectDetail settings, Editor, the legacy
// TranslateProject page, and the pre-translate modal).
//
// Keeping the labels/descriptors here means "OpenAI (Pro)" is branded the same
// everywhere, and the premium-gating rule lives in one place instead of being
// re-derived in each component. The `premium` flag maps to the plan feature
// `premiumAI` (see config/accountPlans.js) — engines marked premium are locked
// unless the workspace plan includes premium AI.

export const TRANSLATION_ENGINES = [
    {
        value: 'deepl',
        label: 'DeepL',
        description: 'Fast neural MT. Formality control on supported languages.',
        premium: false,
    },
    {
        value: 'google',
        label: 'Google Cloud',
        description: 'Broadest language coverage. Fast neural MT.',
        premium: false,
    },
    {
        value: 'openai',
        label: 'OpenAI (Pro)',
        // Surfaced next to the label so the picker reads as a distinct premium,
        // context-aware tier rather than a bare vendor name.
        description: 'Context-aware AI translation — applies brand voice, glossary and tone.',
        premium: true,
    },
];

// The plan-feature flag that unlocks premium engines.
export const PREMIUM_ENGINE_FEATURE = 'premiumAI';

// True when an engine is gated off for the given plan features object. Mirrors
// the backend gate in openaiController (`premiumAI === false` blocks). Only an
// explicit false locks it, so unknown/legacy plans keep working.
export const isEngineLocked = (engine, planFeatures) => {
    if (!engine || !engine.premium) return false;
    return !!planFeatures && planFeatures[PREMIUM_ENGINE_FEATURE] === false;
};

// Convenience: look up a single engine by its stored value (e.g. 'openai').
export const getEngine = (value) =>
    TRANSLATION_ENGINES.find((e) => e.value === value) || null;

// Human label for a stored engine value, with a sensible fallback.
export const engineLabel = (value) => {
    const e = getEngine(value);
    return e ? e.label : (value || 'None');
};
